import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { MenuItem, Select, Input, Chip, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from '@unform/core';
import { find, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import { t } from '~/helpers';

import { canURL } from '~/helpers';
import api from '~/services/api';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function ComboboxMultiple({
  unique,
  label,
  url,
  params,
  title,
  name,
  first,
  hiddens,
  getValue,
  disabled,
  onLoadingChange,
  onLoadingError,
  ehTraduzido,
  defaultValue: defaultValueProp,
  ...rest
}) {
  const classes = useStyles();
  const permission = isEqual(canURL(), { read: true });

  const [data, setData] = useState([]);
  const [personName, setPersonName] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [oldParams, setOldParams] = useState({});

  const inputRef = useRef(null);

  const {
    fieldName,
    defaultValue: fieldDefaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const defaultValue = defaultValueProp ?? fieldDefaultValue;

  const findOption = (value) =>
    find(data, (option) => String(option[unique]) === String(value));

  const normalizaValoresSelecionados = (values = []) =>
    values.map((value) => findOption(value)?.[unique] ?? value);

  function handleChangeValue(event) {
    const { value } = event.target;

    setPersonName(value);
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    async function getData() {
      try {
        onLoadingChange(true);
        await setLoading(true);
        const response = await api.get(url, {
          params: { ...params, pagesize: 999999, pesquisalista: true },
          paramsSerializer: (param) => qs.stringify(param),
        });

        await setData(response.data.items);
        setOldParams(params);
      } catch (err) {
        onLoadingError(err);
        if (err?.erro) {
          toast.error(err.erro);
        }
      } finally {
        await setLoading(false);
        onLoadingChange(false);
      }
    }

    if (!isEmpty(url)) {
      getData();
    }
  }, []);

  useEffect(() => {
    if (!isEqual(params, oldParams) && !loading && !isEmpty(oldParams)) {
      async function getData() {
        try {
          onLoadingChange(true);
          await setLoading(true);
          const response = await api.get(url, {
            params: { ...params, pagesize: 999999, pesquisalista: true },
            paramsSerializer: (param) => qs.stringify(param),
          });

          await setData(response.data.items);
          setOldParams(params);
        } catch (err) {
          onLoadingError(err);
          if (err?.erro) {
            toast.error(err.erro);
          }
        } finally {
          await setLoading(false);
          onLoadingChange(false);
        }
      }

      if (!isEmpty(url)) {
        getData();
      }
    }
  }, [url, params, oldParams, loading, onLoadingChange]);

  useEffect(() => {
    const selectedOptions = isEmpty(url)
      ? personName
      : personName
          .map(findOption)
          .filter(Boolean);

    getValue(personName, selectedOptions);
    inputRef.current.value = personName;
  }, [personName, data]);

  useEffect(() => {
    if (!isEmpty(defaultValue) && !isEmpty(data)) {
      setPersonName(normalizaValoresSelecionados(defaultValue));
    }

    if (!isEmpty(defaultValue) && isEmpty(url)) {
      setPersonName(defaultValue);
    }
  }, [defaultValue, data]);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <>
      <InputLabel>{title}</InputLabel>
      <Select
        multiple
        ref={inputRef}
        onChange={handleChangeValue}
        error={!!error}
        value={personName}
        helperText={error}
        variant="outlined"
        fullWidth
        size="small"
        input={<Input id="select-multiple-chip" />}
        InputLabelProps={{
          shrink: true,
        }}
        renderValue={(selected) => (
          <div className={classes.chips}>
            {selected.map((value) => (
              <Chip
                key={value}
                label={
                  isEmpty(url)
                    ? value
                    : ehTraduzido
                    ? t(findOption(value)?.[label] || '')
                    : findOption(value)?.[label] || ''
                }
                className={classes.chip}
              />
            ))}
          </div>
        )}
        MenuProps={MenuProps}
        {...rest}
        disabled={disabled || permission}
        onFocus={clearError}
      >
        {first?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {ehTraduzido?t(option.label):option.label}
          </MenuItem>
        ))}

        {data?.map((option) => (
          <MenuItem key={option[unique]} value={option[unique]}>
            {ehTraduzido?t(option[label]):option[label]}
          </MenuItem>
        ))}
      </Select>
    </>
  );
}

ComboboxMultiple.propTypes = {
  unique: PropTypes.string,
  label: PropTypes.string,
  url: PropTypes.string,
  params: PropTypes.objectOf(PropTypes.object),
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  first: PropTypes.arrayOf(PropTypes.object),
  hiddens: PropTypes.arrayOf(PropTypes.string),
  getValue: PropTypes.func,
  disabled: PropTypes.bool,
  onLoadingChange: PropTypes.func,
  onLoadingError: PropTypes.func,
  ehTraduzido: PropTypes.bool,
  defaultValue: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

ComboboxMultiple.defaultProps = {
  unique: '',
  label: '',
  url: '',
  params: {},
  title: 'Select',
  first: [],
  hiddens: [],
  getValue: () => {},
  disabled: false,
  onLoadingChange: () => {},
  onLoadingError: () => {},
  ehTraduzido: false,
};

export default ComboboxMultiple;
